import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HasStore } from '@ng-cloud/badger-core/models/interfaces/has-store';
import { Map } from '@ng-cloud/badger-core/models/map';
import { Network } from '@ng-cloud/badger-core/models/network';
import { OctoMap } from '@ng-cloud/badger-core/models/octo-map';
import { Path } from '@ng-cloud/badger-core/models/path';
import { PlayExecution } from '@ng-cloud/badger-core/models/play-execution';
import { Pose } from '@ng-cloud/badger-core/models/pose';
import { ScanConfig } from '@ng-cloud/badger-core/models/scan-config';
import { ScanZone } from '@ng-cloud/badger-core/models/scan-zone';
import { Store } from '@ng-cloud/badger-core/models/store';
import { StoreScheduleEventInstance } from '@ng-cloud/badger-core/models/store-schedule-event-instance';
import { StoreZone } from '@ng-cloud/badger-core/models/store-zone';
import { ApiService, PagedResult } from '@ng-cloud/badger-core/services/api.service';
import * as _ from 'lodash';
import { Observable } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';

@Injectable()
export class StoreService extends ApiService {

  /**
   * Fetch all Stores
   */
  getStores(params?: any): Observable<Store[]> {
    return this.list(Store, 'stores', params, 300);
  }

  /**
   * Fetch Store data for the given id
   */
  getStore(id: number): Observable<Store> {
    return this.get(Store, `stores/${id}`, 600);
  }

  /**
   * Creates a Store
   */
  createStore(store: Store): Observable<Store> {
    return this.create(store, 'stores');
  }

  /**
   * Update Store
   */
  updateStore(store: Store): Observable<Store> {
    return this.update(store, `stores/${store.id}`);
  }

  /**
   * Fetch all store maps data for the given store id
   */
  getMaps(id: number, params?: any): Observable<Map[]> {
    return this.list(Map, `stores/${id}/maps`, params, 600);
  }

  /**
   * Fetch store map data for the given store id
   */
  getMap(id: number, params?: any): Observable<Map> {
    return this.get(Map, `stores/${id}/map`, 600, params);
  }

  downloadMap(storeId: number, mapType?: string): Observable<any> {
    return this.http.get(this.url(`stores/${storeId}/map/download`), {
      responseType: 'arraybuffer',
      params: mapType ? { map_type: mapType } : {}
    }).pipe(catchError((err) => this.handleError(err)));
  }

  addStores(models: HasStore[]): Observable<Store[]> {
    const modelsByStoreId = _.groupBy(models, 'storeId');
    const ids = _.uniq(models.map(model => model.storeId)).sort();
    return this.collectObservable(_.chunk(ids, 200), (idChunk) => this.getStores({ 'id[]': idChunk }).pipe(
      tap((stores: Store[]) => {
        stores.forEach(store => {
          const storeModels = modelsByStoreId[store.id];
          storeModels.forEach(model => model.store = store);
        });
      })
    ));
  }

  addStore(model: HasStore): Observable<Store> {
    return this.getStore(model.storeId).pipe(tap(store => model.store = store));
  }

  addMaps(stores: Store[]): Observable<Map> {
    return this.collectObservable(stores, store => this.addMap(store));
  }

  addMap(store: Store): Observable<Map> {
    return this.getMap(store.id).pipe(tap(storeMap => store.map = storeMap));
  }

  uploadMap(formData: FormData, id: number): Observable<Map> {
    let headers = new HttpHeaders();
    headers = headers.append('enctype', 'multipart/form-data');

    return this.http.post(this.url(`stores/${id}/maps`), formData, { headers: headers }).pipe(
      map(response => new Map().deserialize(response)),
      catchError(e => this.handleError(e)));
  }

  uploadOctoMap(formData: FormData, id: number): Observable<OctoMap> {
    let headers = new HttpHeaders();
    headers = headers.append('enctype', 'multipart/form-data');

    return this.http.post(this.url(`stores/${id}/octo_maps`), formData, { headers: headers }).pipe(
      map(response => new OctoMap().deserialize(response)),
      catchError(e => this.handleError(e)));
  }

  getOctoMaps(storeId: number, params?: any): Observable<OctoMap[]> {
    return this.list(OctoMap, `stores/${storeId}/octo_maps`, 600, params);
  }

  getOctoMap(storeId: number, octoMapId: number, params?: any): Observable<OctoMap> {
    return this.get(OctoMap, `stores/${storeId}/octo_maps/${octoMapId}`, 600, params);
  }

  getNetwork(storeId: number): Observable<Network> {
    return this.withCache(`stores/${storeId}/network`, {}, 600, () => this.http.get(this.url(`stores/${storeId}/network`))).pipe(
      map(response => {
        const network = new Network();
        const json = response;
        if (json) {
          network.deserialize(json);
        }
        return network;
      }),
      catchError(e => this.handleError(e)));
  }

  modifyNetwork(storeId: number, network: Network): Observable<Network> {
    return this.create(network, `stores/${storeId}/network`);
  }

  getZones(id: number, params?: any): Observable<StoreZone[]> {
    return this.list(StoreZone, `stores/${id}/zones`, params, 600);
  }

  modifyZones(id: number, zones: StoreZone[]): Observable<StoreZone[]> {
    const params = {
      store_zones_attributes: zones.map(z => z.serialize())
    };
    return this.http.post<StoreZone[]>(this.url(`stores/${id}/zones`), params).pipe(
      tap(() => this.deleteCachedRequests(`stores/${id}/zones`)),
      map((response: any[]) => response.map(source => new StoreZone().deserialize(source))),
      catchError(e => this.handleError(e)));
  }

  getScanZones(id: number, params?: any): Observable<ScanZone[]> {
    return this.list(ScanZone, `stores/${id}/scan_zones`, params, 600);
  }

  modifyScanZones(id: number, zones: ScanZone[], zoneType?: string): Observable<ScanZone[]> {
    if (!zoneType) {
      zoneType = zones[0].zoneType;
    }
    const params = {
      zone_type: zoneType,
      store_scan_zones_attributes: zones.map(z => z.serialize())
    };
    return this.http.post<ScanZone[]>(this.url(`stores/${id}/scan_zones`), params).pipe(
      tap(() => this.deleteCachedRequests(`stores/${id}/scan_zones`)),
      map((response: any[]) => response.map(source => new ScanZone().deserialize(source))),
      catchError(e => this.handleError(e)));
  }

  getPaths(id: number): Observable<Path[]> {
    return this.list(Path, `stores/${id}/paths`, 600);
  }

  modifyPaths(id: number, paths: Path[]): Observable<Path[]> {
    const params = {
      paths_attributes: paths.map(p => p.serialize())
    };
    return this.http.post<Path[]>(this.url(`stores/${id}/paths`), params).pipe(
      tap(() => this.deleteCachedRequests(`stores/${id}/paths`)),
      map((response: any[]) => response.map(source => new Path().deserialize(source))),
      catchError(e => this.handleError(e)));
  }

  getOrderedScanConfigs(store: Store, scannableId: number = null, scannable_type: string = null): Observable<ScanConfig[]> {
    const url = `stores/${store.id}/ordered_scan_configs`;
    const params = {};
    if (scannableId) {
      params['scannable_id'] = scannableId;
      params['scannable_type'] = scannable_type || 'Path';
    }
    return this.http.get<ScanConfig[]>(this.url(url), { params: params }).pipe(
      tap(() => this.deleteCachedRequests(url)),
      map((response: any[]) => response.map(source => new ScanConfig().deserialize(source))),
      catchError(e => this.handleError(e)));
  }

  getPoses(id: number, params?: any): Observable<Pose[]> {
    return this.list(Pose, `stores/${id}/poses`, params, 600);
  }

  modifyPoses(id: number, poses: Pose[], poseType?: string): Observable<Pose[]> {
    if (!poseType) {
      poseType = poses[0]?.poseType;
    }
    const params = {
      pose_type: poseType,
      poses: poses.map(z => z.serialize())
    };
    return this.http.post<Pose[]>(this.url(`stores/${id}/poses`), params).pipe(
      tap(() => this.deleteCachedRequests(`stores/${id}/poses`)),
      map((response: any[]) => response.map(source => new Pose().deserialize(source))),
      catchError(e => this.handleError(e)));
  }

  // An endpoint to get a store-wide list of heartbeats that are mapped to the robot_id they belong to
  getHeartbeats(storeId: number, params: any = {}): Observable<any> {
    return this.http.get(this.url(`stores/${storeId}/heartbeat_overview`), { params: ApiService.params(params) }).pipe(
      catchError((err) => this.handleError(err))
    );
  }

  /**
   * Paginate playExecutions list
   */
  pagePlayExecutions(storeId: number, params?: any): Observable<PagedResult<PlayExecution>> {
    return this.page(PlayExecution, `stores/${storeId}/play_executions/page`, params);
  }

  getPlayExecutions(storeId: number, params?: any): Observable<PlayExecution[]> {
    return this.list(PlayExecution, `stores/${storeId}/play_executions`, params, 5);
  }

  /**
   * Open websocket connection to listen for PlayExecutionCreated messages for given store id
   *
   * @param  id store id
   * @return stream of newly created play executions for a store
   */
  playExecutionCreated(id: number): Observable<PlayExecution> {
    return this.channel(PlayExecution, 'PlayExecutionCreatedChannel', { store_id: id }).pipe(
      tap(() => this.deleteCachedRequests([`play_executions`, `views/stores/${id}/play_executions/page`])));
  }

  /**
   * Open websocket connection to listen for PlayExecutionUpdated messages for given store id
   *
   * @param  id store id
   * @return stream of updated PlayExecutions for a store
   */
  playExecutionUpdated(id: number): Observable<PlayExecution> {
    return this.channel(PlayExecution, 'PlayExecutionUpdatedChannel', { store_id: id }).pipe(
      tap(() => this.deleteCachedRequests([`play_executions`, `views/stores/${id}/play_executions/page`])));
  }

  runPlay(storeId: number, playId: number, params?: any) {
    return this.http.post(this.url(`stores/${storeId}/plays/${playId}/run`), params).pipe(
      catchError(err => this.handleError(err)));
  }

  queuePlay(storeId: number, params?: any): Observable<any> {
    return this.http.post(this.url(`stores/${storeId}/queue_play`), params).pipe(
      catchError(err => this.handleError(err)));
  }

  updateQueue(storeId: number, params?: any): Observable<PlayExecution[]> {
    return this.http.post<PlayExecution[]>(this.url(`stores/${storeId}/play_executions/update_queue`), params).pipe(
      filter(response => !_.isNil(response)),
      map((response: any[]) => response.map(source => new PlayExecution().deserialize(source))),
      catchError(err => this.handleError(err)));
  }

  savePlayExecution(storeId: number, executionId: number, params?: any) {
    return this.http.post(this.url(`stores/${storeId}/play_executions/${executionId}/save_execution`), params).pipe(
      catchError(err => this.handleError(err)));
  }

  cancelPlayExecution(storeId: number, executionId: number, params?: any) {
    return this.http.post(this.url(`stores/${storeId}/play_executions/${executionId}/cancel_execution`), params).pipe(
      catchError(err => this.handleError(err)));
  }

  downloadZonePdf(storeId: number): Observable<any> {
    return this.http.get(this.url(`stores/${storeId}/zone_export`), {
      responseType: 'arraybuffer'
    }).pipe(catchError((err) => this.handleError(err)));
  }

  getStoreSaltConfig(storeId: number): Observable<any> {
    return this.http.get(this.url(`stores/${storeId}/salt_config`)).pipe(
      catchError((err) => this.handleError(err))
    );
  }

  /**
   * Returns scheduled event instances of type play for store
   * @param storeId
   * @param params - can include play_type (insight/rfid/cleansweep, defaults to all),
   *            start_time (defaults to now), end_time (defaults to end of day)
   */
  getNextPlays(storeId: number, params?: any): Observable<StoreScheduleEventInstance[]> {
    return this.list(StoreScheduleEventInstance, `stores/${storeId}/next_plays`, params);
  }
}
